import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { loadNamespace, isNamespaceLoaded, markAsLoaded, INITIAL_NAMESPACES } from '@/i18n/lazyNamespaces';

type Namespace = 'common' | 'auth' | 'legal' | 'navigation' | 'listings' | 'offers' | 'profile' | 'admin' | 'platform' | 'allocations';
type Language = 'en' | 'sv';

/**
 * Hook to lazily load i18n namespaces on-demand.
 * 
 * Features:
 * - Deduplication: Multiple components requesting the same namespace won't trigger duplicate imports
 * - Language change handling: Automatically loads namespaces when language changes
 * - Fail-safe: If a namespace fails to load, the hook returns ready=true anyway (keys will be shown as fallback)
 * - Returns ready: boolean to allow showing skeletons while loading (optional)
 * 
 * Usage:
 * const { ready } = useNamespace(['listings', 'navigation']);
 * // Optionally show skeleton while !ready
 */
export function useNamespace(namespaces: Namespace[]): { ready: boolean } {
  const { i18n } = useTranslation();
  const [ready, setReady] = useState(() => {
    // Check if all requested namespaces are already loaded
    const lang = i18n.language as Language;
    return namespaces.every(ns => 
      INITIAL_NAMESPACES.includes(ns) || isNamespaceLoaded(lang, ns)
    );
  });
  
  // Track which language we've loaded for to handle language changes
  const loadedForLang = useRef<string | null>(null);
  
  useEffect(() => {
    const lang = (i18n.language || 'en') as Language;
    
    // Skip if already loaded for this language
    if (loadedForLang.current === lang && ready) {
      return;
    }
    
    // Filter to only namespaces that need loading
    const namespacesToLoad = namespaces.filter(ns => {
      // Initial namespaces are always available
      if (INITIAL_NAMESPACES.includes(ns)) return false;
      // Check if already loaded for current language
      return !isNamespaceLoaded(lang, ns);
    });
    
    // If nothing to load, mark as ready
    if (namespacesToLoad.length === 0) {
      loadedForLang.current = lang;
      setReady(true);
      return;
    }
    
    // Start loading
    setReady(false);
    
    const loadAll = async () => {
      const results = await Promise.all(
        namespacesToLoad.map(async (ns) => {
          const data = await loadNamespace(lang, ns);
          if (data) {
            // Add the resource bundle to i18next
            i18n.addResourceBundle(lang, ns, data, true, true);
          }
          return { ns, success: data !== null };
        })
      );
      
      // Log any failures (but don't block ready state)
      results.forEach(({ ns, success }) => {
        if (!success && !isNamespaceLoaded(lang, ns)) {
          console.warn(`[useNamespace] Namespace "${ns}" failed to load for "${lang}"`);
        }
      });
      
      loadedForLang.current = lang;
      setReady(true);
    };
    
    loadAll();
  }, [i18n.language, namespaces.join(','), i18n]);
  
  return { ready };
}

/**
 * Mark initial namespaces as loaded for both languages.
 * Call this once during app initialization.
 */
export function markInitialNamespacesLoaded(): void {
  const languages: Language[] = ['en', 'sv'];
  INITIAL_NAMESPACES.forEach(ns => {
    languages.forEach(lang => {
      markAsLoaded(lang, ns as Namespace);
    });
  });
}
